/**
 * PATH: packages/ui/src/table/SortableTableHeader.tsx
 * FILE_ID: UI-TABLE-SORTHEADER-001
 * PURPOSE: The one sortable column header for every Quotech table.
 * WHY: 27 hand-rolled headers were in production across six hubs, agreeing on
 *      neither icon, accent, ARIA, nor direction vocabulary. This takes the
 *      People directory's look (operator mandate) and the job-hub Rich
 *      tracker's accessibility, rather than the intersection of the two.
 * FLOW: caller sort state -> toSortRules -> aria-sort + glyph -> onSort intent
 * DEPENDENCIES: lucide-react (via SortIcon), ../lib/utils (cn)
 * RELATED: sortRules.ts (pure maths), SortIcon.tsx, SortPriorityBadge.tsx,
 *   tableHeaderChrome.ts (the People paint)
 */
import type {
  ComponentType,
  CSSProperties,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  Ref,
} from 'react'

import { cn } from '../lib/utils'
import { SortIcon } from './SortIcon'
import { SortPriorityBadge } from './SortPriorityBadge'
import { TABLE_SORT_BUTTON, TABLE_TH } from './tableHeaderChrome'
import {
  ariaSortFor,
  sortButtonLabel,
  sortDirOf,
  sortRankOf,
  toSortRules,
} from './sortRules'
import type { SortAlign, SortDir, SortRule } from './types'

export type SortableTableHeaderProps<K extends string = string> = {
  /**
   * Plain text, because it also becomes the control's accessible name. Put
   * decoration (info buttons, filter glyphs) in `trailing` instead.
   */
  label: string
  /** This column's sort key. Also published as `data-header-column-id`. */
  sortKey: K
  /** Multi-column sort state. Supply this OR `activeKey`/`activeDir`. */
  rules?: readonly SortRule<K>[]
  /** Single-column sort state: the key the table is currently sorted by. */
  activeKey?: K | null
  /** Single-column sort state: defaults to 'asc' when omitted. */
  activeDir?: SortDir
  /** `append` is true when the operator held Shift, meaning "add a sort key". */
  onSort: (key: K, append: boolean) => void
  /** False renders the label as static text with no control and no glyph. */
  sortable?: boolean
  align?: SortAlign
  /** Merged over the People header paint; Tailwind conflicts resolve to yours. */
  className?: string
  buttonClassName?: string
  style?: CSSProperties
  title?: string
  /** Column identity when it differs from the sort key (frozen/reorder panes). */
  columnId?: string
  onHeaderContextMenu?: (key: K, label: string, event: MouseEvent) => void
  /** Rendered after the sort glyph, inside the header cell. */
  trailing?: ReactNode
  /**
   * 'div' for flex/grid layouts that are not real tables. It swaps `scope="col"`
   * for `role="columnheader"` and keeps everything else, including `aria-sort`.
   * Only valid inside an ancestor `role="row"`; a bare div with this role reads
   * worse to a screen reader than an unlabelled one.
   */
  as?: 'th' | 'div'
  /**
   * The rendered header cell. Named for the default `th`; with `as="div"` it is
   * that div. Exists because column reorder needs a ref to the cell the
   * primitive owns - without it callers resolve the node by querySelector.
   */
  thRef?: Ref<HTMLElement>
  /**
   * Spread onto the header cell, for dnd `draggableProps` and native drag
   * handlers. `className` and `style` here merge with (and lose ties to) the
   * matching props, so passing dnd's transform style does not erase the
   * caller's width.
   *
   * `data-*` is admitted explicitly: React allows it in JSX but not in a value
   * typed as bare `HTMLAttributes`, and a surface that publishes its drag state
   * as an attribute (`data-column-dragging`) would otherwise need a cast at
   * every call site. `data-column-id` and `data-header-column-id` stay the
   * primitive's, whatever is passed here.
   */
  thProps?: HTMLAttributes<HTMLElement>
    & Partial<Record<`data-${string}`, string | number | boolean | undefined>>
}

const ALIGN_CELL: Record<SortAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
}

const ALIGN_CONTROL: Record<SortAlign, string> = {
  left: '',
  right: 'w-full flex-row-reverse justify-start',
  center: 'w-full justify-center',
}

export function SortableTableHeader<K extends string = string>({
  label,
  sortKey,
  rules,
  activeKey,
  activeDir,
  onSort,
  sortable = true,
  align = 'left',
  className,
  buttonClassName,
  style,
  title,
  columnId,
  onHeaderContextMenu,
  trailing,
  as = 'th',
  thRef,
  thProps,
}: SortableTableHeaderProps<K>) {
  const resolved = toSortRules(rules, activeKey, activeDir)
  const dir = sortDirOf(resolved, sortKey)
  const rank = sortRankOf(resolved, sortKey)

  /**
   * A union-typed intrinsic tag makes TS demand props valid for `th` and `div`
   * simultaneously, and their ref types disagree (`HTMLTableHeaderCellElement`
   * vs `HTMLDivElement`). Casting keeps one render path instead of two nearly
   * identical JSX branches; every prop below is valid on either element.
   */
  const Cell = as as unknown as ComponentType<Record<string, unknown>>

  /**
   * Pulled out so passthrough paint merges instead of replacing, and so an
   * absent `title`/context menu cannot clobber one supplied via `thProps`.
   */
  const { className: passedClassName, style: passedStyle, ...passedRest } = thProps ?? {}

  const cellProps = {
    ...passedRest,
    ref: thRef,
    ...(as === 'th' ? { scope: 'col' as const } : { role: 'columnheader' }),
    className: cn(TABLE_TH, ALIGN_CELL[align], passedClassName, className),
    style: { ...passedStyle, ...style },
    ...(title === undefined ? {} : { title }),
    'data-column-id': columnId,
    'data-header-column-id': sortKey,
    ...(onHeaderContextMenu
      ? {
          onContextMenu: (event: MouseEvent) =>
            onHeaderContextMenu(sortKey, label, event),
        }
      : {}),
  }

  if (!sortable) {
    return (
      <Cell {...cellProps}>
        <span>{label}</span>
        {trailing}
      </Cell>
    )
  }

  /**
   * Enter and Space are handled explicitly so Shift can mean "append this key
   * to the sort" from the keyboard too. preventDefault stops the browser's own
   * click synthesis, so onSort still fires exactly once per activation.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onSort(sortKey, event.shiftKey)
  }

  return (
    <Cell {...cellProps} aria-sort={ariaSortFor(resolved, sortKey)}>
      <button
        type="button"
        onClick={(event) => onSort(sortKey, event.shiftKey)}
        onKeyDown={handleKeyDown}
        aria-label={sortButtonLabel(label, dir)}
        className={cn(TABLE_SORT_BUTTON, ALIGN_CONTROL[align], buttonClassName)}
      >
        {label}
        <SortIcon dir={dir} />
        <SortPriorityBadge rank={rank} />
      </button>
      {trailing}
    </Cell>
  )
}
